// Configuration du métier "Artisan polyvalent" (4ème métier MVP).
// Source de vérité pour les codes de sous-spécialités, labels, et mappings.

// ---------------------------------------------------------------------------
// Codes des sous-spécialités
// ---------------------------------------------------------------------------

export const HANDYMAN_SPECIALTY_CODES = [
  'artisan_polyvalent',
  'small_plumbing',
  'lighting_installation',
  'furniture_assembly',
  'door_repair',
  'odd_jobs',
  'other',
] as const;

export type HandymanSpecialtyCode = (typeof HANDYMAN_SPECIALTY_CODES)[number];

// ---------------------------------------------------------------------------
// Tableau des sous-spécialités (ordre d'affichage)
// ---------------------------------------------------------------------------

export const HANDYMAN_SPECIALTIES: {
  code: HandymanSpecialtyCode;
  label_fr: string;
  label_ar: string;
  is_other: boolean;
  display_order: number;
}[] = [
  { code: 'artisan_polyvalent',    label_fr: 'Artisan polyvalent',      label_ar: 'حرفي متعدد التخصصات',   is_other: false, display_order: 0 },
  { code: 'small_plumbing',        label_fr: 'Petite plomberie',        label_ar: 'السباكة الخفيفة',        is_other: false, display_order: 1 },
  { code: 'lighting_installation', label_fr: 'Installation luminaires',  label_ar: 'تركيب الإضاءة',          is_other: false, display_order: 2 },
  { code: 'furniture_assembly',    label_fr: 'Montage meubles',           label_ar: 'تجميع الأثاث',           is_other: false, display_order: 3 },
  { code: 'door_repair',           label_fr: 'Électricité légère',        label_ar: 'الكهرباء الخفيفة',       is_other: false, display_order: 4 },
  { code: 'odd_jobs',              label_fr: 'Petits travaux divers',     label_ar: 'أعمال متنوعة',            is_other: false, display_order: 5 },
  { code: 'other',                 label_fr: 'Autre spécialité',          label_ar: 'تخصص آخر',               is_other: true,  display_order: 6 },
];

// ---------------------------------------------------------------------------
// Mapping sous-spécialité → métier spécialiste (cross-visibilité)
// ---------------------------------------------------------------------------
// Un artisan polyvalent ayant coché une sous-spécialité ici apparaît dans
// les résultats de recherche pour le métier spécialiste correspondant.
// Les codes sans mapping (furniture_assembly, odd_jobs, other) sont
// exclusivement visibles dans la recherche "Artisan polyvalent".

export const SPECIALTY_TO_METIER: Partial<Record<HandymanSpecialtyCode, string>> = {
  small_plumbing:        'Plomberie',
  lighting_installation: 'Électricité',
  door_repair:           'Électricité',  // "Électricité légère" → cross-visibilité Électricité
};

/**
 * Retourne les codes de sous-spécialités qui correspondent à un métier spécialiste donné.
 * Utilisé par useSearchArtisans pour la cross-visibilité.
 *
 * @example
 * getSpecialtyCodesForMetier("Plomberie") // → ["small_plumbing"]
 */
export function getSpecialtyCodesForMetier(metier: string): HandymanSpecialtyCode[] {
  return (Object.entries(SPECIALTY_TO_METIER) as [HandymanSpecialtyCode, string][])
    .filter(([, m]) => m === metier)
    .map(([code]) => code);
}

// ---------------------------------------------------------------------------
// Types pour la sélection dans les formulaires
// ---------------------------------------------------------------------------

export interface HandymanSpecialtySelection {
  specialty_code: HandymanSpecialtyCode;
  other_specialty_label?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Retourne le libellé français d'un code de sous-spécialité. */
export function getHandymanLabel(
  code: HandymanSpecialtyCode,
  otherLabel?: string | null
): string {
  if (code === 'other' && otherLabel) return otherLabel;
  return HANDYMAN_SPECIALTIES.find((s) => s.code === code)?.label_fr ?? code;
}
